import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.5.
 *
 * The only layout that renders OUTSIDE `AppShell`: login, signup and accepting
 * an invitation have no session yet, so there is no nav, no tenant switcher and
 * no environment badge to show. That is why it does not compose with `AppShell`
 * — it exists apart.
 *
 * `title` is the screen's `<h1>`, not a card label: these views have neither a
 * `PageHeader` nor an `AppShell` contributing another heading, so the whole
 * page's heading hierarchy starts (and ends) here.
 *
 * `brand` (the TCIT mark) and `footer` are received by composition, already
 * assembled by the caller: this component does not know what the logo is or what
 * the footer says, and so it does not risk an empty layout when they are absent
 * — nothing is simply rendered in their place.
 */
const cardVariants = cva(
  // `shadow-raised`: the card RESTS on the page, it does not float. The three
  // elevation steps are chosen by what the element IS, not by how much shadow is
  // wanted — see `theme.css`.
  'w-full rounded-lg border border-border bg-surface p-inset-lg shadow-raised',
  {
    variants: {
      width: {
        // There is (not yet) a width role dedicated to an "authentication card"
        // in the tokens. The popup widths are reused (400px / 640px — see
        // `--spacing-popup-*` in theme.css and `Dialog`'s contract in §5.6),
        // which are the only component-width scale they expose today.
        // TODO(tokens): if this feels borrowed, the missing role is one of its
        // own for card widths, not one more popup.
        sm: 'max-w-popup-sm',
        md: 'max-w-popup-md',
      },
    },
    defaultVariants: {
      width: 'sm',
    },
  },
);

export interface CenteredCardLayoutProps {
  title: string;
  description?: React.ReactNode;
  /** The TCIT mark above the card. The logo goes here, not in `AppShell`. */
  brand?: React.ReactNode;
  /** «¿Ya tienes cuenta? Inicia sesión» */
  footer?: React.ReactNode;
  width?: 'sm' | 'md';
  children: React.ReactNode;
}

export function CenteredCardLayout({
  title,
  description,
  brand,
  footer,
  width,
  children,
}: CenteredCardLayoutProps): React.JSX.Element {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-stack bg-page p-inset">
      {brand ?? null}
      <div className={cn(cardVariants({ width }))}>
        <div className="flex flex-col gap-stack">
          <div className="flex flex-col gap-stack-sm">
            <h1 className="text-h2 text-fg">{title}</h1>
            {description ? (
              <div className="text-body-s text-fg-muted">{description}</div>
            ) : null}
          </div>
          {children}
        </div>
      </div>
      {footer ?? null}
    </main>
  );
}
